import rtti from '@/sui/tools/rtti'
import assert from '@/sui/tools/assert'

import checker from '@/sui/tools/checker'

const debug = process.env.VUE_APP_SPIADI_DEBUG === '1'

function applyOnHierarchy (object, depth, func) {
  if (rtti.isObject(object)) {
    const stack = [{ target: object, targetDepth: 1 }]
    while (stack.length > 0) {
      const { target, targetDepth } = stack.pop()
      if (target == null || !rtti.isArrayOrObject(target)) {
        continue
      }

      func(target)

      if (targetDepth > depth) {
        continue
      }

      if (!rtti.isArray(target)) {
        for (let key in target) {
          const next = target[key]
          if (rtti.isArrayOrObject(next)) {
            stack.push({ target: next, targetDepth: targetDepth + 1 })
          }
        }
        continue
      }

      for (let i = 0; i !== target.length; ++i) {
        const item = target[i]
        if (rtti.isArrayOrObject(item)) {
          stack.push({ target: item, targetDepth: targetDepth + 1 })
        }
      }
    }
  }

  return func(object)
}

export function freeze (object, depth = 1) {
  return applyOnHierarchy(object, depth, Object.freeze)
}

export function seal (object, depth = 1) {
  return applyOnHierarchy(object, depth, Object.seal)
}

if (debug) {
  const c = checker('ice')
  const testObject = () => {
    return {
      array: [
        {
          subarray: [{}]
        }
      ],
      object: {
        subarray: [{}],
        subobject: {
        }
      }
    }
  }

  const tests = [
    { t: (f) => Object.isFrozen(f), m: 'test object' },
    { t: (f) => Object.isFrozen(f.array), m: 'frozen.array' },
    { t: (f) => Object.isFrozen(f.array[0]), m: 'frozen.array[0]' },
    { t: (f) => Object.isFrozen(f.array[0].subarray), m: 'frozen.array[0].subarray' },
    { t: (f) => Object.isFrozen(f.array[0].subarray[0]), m: 'frozen.array[0].subarray[0]' },
    { t: (f) => Object.isFrozen(f.object), m: 'frozen.object' },
    { t: (f) => Object.isFrozen(f.object.subarray), m: 'frozen.object.subarray' },
    { t: (f) => Object.isFrozen(f.object.subarray[0]), m: 'frozen.object.subarray[0]' },
    { t: (f) => Object.isFrozen(f.object.subobject), m: 'frozen.object.subobject' }
  ]

  const loop = [
    {
      ctx: 'TestBasicFreeze',
      expected: [true, true, false, false, false, true, false, false, false]
    },
    {
      ctx: 'TestFreezeLevel2',
      depth: 2,
      expected: [true, true, true, false, false, true, true, false, true]
    },
    {
      ctx: 'TestFreezeLevel3',
      depth: 3,
      expected: [true, true, true, true, false, true, true, true, true]
    }
  ]

  for (let i = 0; i !== loop.length; ++i) {
    const l = loop[i]

    c.ctx = l.ctx
    const frozen = testObject()
    c.check(!Object.isFrozen(frozen), 'initial value not frozen')
    if (l.depth == null) {
      freeze(frozen)
    } else {
      freeze(frozen, l.depth)
    }

    assert.equals(tests.length, l.expected.length)
    for (let j = 0; j !== tests.length; ++j) {
      const test = tests[j]
      const value = l.expected[j]
      c.check(
        test.t(frozen) === value,
        `${test.m} should ${value ? 'be' : 'not be'} frozen`
      )
    }
  }

  c.end()
}

export default {
  freeze,
  seal
}
